import React, { createContext, useContext, useState, useCallback } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { X, Check, Loader2 } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const LeadCtx = createContext(null);
export const useLead = () => useContext(LeadCtx);

const ORG_TYPES = [
  "Golf Course", "Municipality", "Airport", "Sports Complex", "University",
  "Sod Farm", "Corporate Campus", "Large Estate", "Roadside Maintenance",
  "Commercial Landscape", "Other",
];

export const LeadProvider = ({ children }) => {
  const [open, setOpen] = useState(false);
  const [type, setType] = useState("demo");
  const openLead = useCallback((t) => { setType(t); setOpen(true); }, []);
  return (
    <LeadCtx.Provider value={{ openLead }}>
      {children}
      <LeadDialog open={open} setOpen={setOpen} type={type} setType={setType} />
    </LeadCtx.Provider>
  );
};

const fieldCls =
  "w-full bg-[#121212] border border-white/10 px-4 py-3 text-white placeholder:text-gray-500 focus:border-[#D62828] focus:outline-none transition-colors text-sm";

const LeadDialog = ({ open, setOpen, type, setType }) => {
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [form, setForm] = useState({
    name: "", email: "", company: "", phone: "",
    organization_type: "", acres: "", message: "",
  });

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const close = () => {
    setOpen(false);
    setTimeout(() => { setDone(false); setForm({ name: "", email: "", company: "", phone: "", organization_type: "", acres: "", message: "" }); }, 300);
  };

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await axios.post(`${API}/leads`, { type, ...form });
      setDone(true);
      toast.success("Request received. Our team will be in touch shortly.");
    } catch (err) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          data-testid="lead-dialog-overlay"
          className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm overflow-y-auto"
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          onClick={close}
        >
          <motion.div
            data-testid="lead-dialog"
            className="relative w-full max-w-lg bg-[#0d0d0d] border border-white/10 my-8"
            initial={{ y: 40, opacity: 0, scale: 0.98 }}
            animate={{ y: 0, opacity: 1, scale: 1 }}
            exit={{ y: 40, opacity: 0, scale: 0.98 }}
            transition={{ type: "spring", damping: 26, stiffness: 280 }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="h-1 w-full bg-[#D62828]" />
            <button data-testid="lead-dialog-close" onClick={close} className="absolute top-5 right-5 text-gray-400 hover:text-white transition-colors">
              <X size={22} />
            </button>

            {done ? (
              <div className="p-12 text-center" data-testid="lead-success">
                <div className="mx-auto mb-6 flex h-16 w-16 items-center justify-center rounded-full bg-[#D62828]">
                  <Check size={32} className="text-white" />
                </div>
                <h3 className="font-heading text-4xl font-extrabold uppercase tracking-tight">Request Sent</h3>
                <p className="mt-3 text-gray-400">A Progressive specialist will reach out within one business day.</p>
                <button onClick={close} data-testid="lead-success-close" className="mt-8 bg-[#D62828] px-8 py-3 text-sm font-bold uppercase tracking-widest text-white hover:bg-[#B91C1C] transition-colors">
                  Done
                </button>
              </div>
            ) : (
              <div className="p-8">
                <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#D62828]">Progressive Navigator</p>
                <h3 className="font-heading text-4xl font-extrabold uppercase tracking-tight mt-1">
                  {type === "demo" ? "Book a Demo" : "Request a Quote"}
                </h3>

                <div className="mt-4 flex gap-2">
                  <button type="button" data-testid="lead-type-demo" onClick={() => setType("demo")}
                    className={`flex-1 py-2 text-xs font-bold uppercase tracking-widest border transition-colors ${type === "demo" ? "bg-[#D62828] border-[#D62828] text-white" : "border-white/15 text-gray-400 hover:text-white"}`}>
                    Demo
                  </button>
                  <button type="button" data-testid="lead-type-quote" onClick={() => setType("quote")}
                    className={`flex-1 py-2 text-xs font-bold uppercase tracking-widest border transition-colors ${type === "quote" ? "bg-[#D62828] border-[#D62828] text-white" : "border-white/15 text-gray-400 hover:text-white"}`}>
                    Quote
                  </button>
                </div>

                <form onSubmit={submit} className="mt-5 space-y-3">
                  <input data-testid="lead-name" required placeholder="Full name *" className={fieldCls} value={form.name} onChange={update("name")} />
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <input data-testid="lead-email" required type="email" placeholder="Work email *" className={fieldCls} value={form.email} onChange={update("email")} />
                    <input data-testid="lead-phone" placeholder="Phone" className={fieldCls} value={form.phone} onChange={update("phone")} />
                  </div>
                  <input data-testid="lead-company" required placeholder="Organization *" className={fieldCls} value={form.company} onChange={update("company")} />
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <select data-testid="lead-org-type" className={fieldCls} value={form.organization_type} onChange={update("organization_type")}>
                      <option value="">Property type</option>
                      {ORG_TYPES.map((o) => <option key={o} value={o}>{o}</option>)}
                    </select>
                    <input data-testid="lead-acres" placeholder="Approx. acreage" className={fieldCls} value={form.acres} onChange={update("acres")} />
                  </div>
                  <textarea data-testid="lead-message" rows={3} placeholder="Tell us about your property..." className={fieldCls} value={form.message} onChange={update("message")} />
                  <button data-testid="lead-submit" type="submit" disabled={submitting}
                    className="w-full bg-[#D62828] py-4 text-sm font-bold uppercase tracking-widest text-white hover:bg-[#B91C1C] transition-colors flex items-center justify-center gap-2 disabled:opacity-60">
                    {submitting ? <><Loader2 size={16} className="animate-spin" /> Sending</> : type === "demo" ? "Book My Demo" : "Get My Quote"}
                  </button>
                </form>
              </div>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
